import { useQuery } from "@tanstack/react-query";
import { partnerDashboardService } from "@/services/partner-dashboard-service";
import { partnerSettingsService } from "@/services/partner-settings-service";
import type {
  PartnerAttributionSource,
  PartnerOrdersCatalogFilter,
  TenantPartnerOrdersSummary,
} from "@/types/partner-order";
import type { HotelPartnerOrdersSummary } from "@/types/hotelService";

export const partnerOrdersKeys = {
  all: ["partnerOrders"] as const,

  hotel: (
    hotelId: string,
    from: string,
    to: string,
    catalog: PartnerOrdersCatalogFilter,
    page: number,
    limit: number,
  ) =>
    [
      ...partnerOrdersKeys.all,
      "hotel",
      hotelId,
      from,
      to,
      catalog,
      page,
      limit,
    ] as const,

  tenant: (
    tenantId: string,
    from: string,
    to: string,
    catalog: PartnerOrdersCatalogFilter,
    page: number,
    limit: number,
  ) =>
    [
      ...partnerOrdersKeys.all,
      "tenant",
      tenantId,
      from,
      to,
      catalog,
      page,
      limit,
    ] as const,
};

/** Single hotel's orders, viewed from the admin billing/revenue drill-down. */
export const useHotelPartnerOrders = (
  hotelId: string | undefined,
  from: string,
  to: string,
  catalog: PartnerOrdersCatalogFilter,
  page: number,
  limit: number,
  // Task 0041 — room-attribution filter, both optional.
  attribution?: PartnerAttributionSource,
  roomId?: string,
) =>
  useQuery({
    queryKey: [
      ...partnerOrdersKeys.hotel(hotelId || "", from, to, catalog, page, limit),
      attribution ?? null,
      roomId ?? null,
    ],
    enabled: Boolean(hotelId),
    queryFn: () =>
      partnerSettingsService.getPartnerOrders(hotelId!, {
        from,
        to,
        catalog,
        page,
        limit,
        attribution,
        roomId,
      }),
    staleTime: 60 * 1000,
  });

/** Orders across every hotel in the tenant. */
export const useTenantPartnerOrders = (
  tenantId: string | undefined,
  from: string,
  to: string,
  catalog: PartnerOrdersCatalogFilter,
  page: number,
  limit: number,
) =>
  useQuery({
    queryKey: partnerOrdersKeys.tenant(
      tenantId || "",
      from,
      to,
      catalog,
      page,
      limit,
    ),
    enabled: Boolean(tenantId),
    queryFn: () =>
      partnerDashboardService.getOrders(tenantId!, {
        from,
        to,
        catalog,
        page,
        limit,
      }),
    staleTime: 60 * 1000,
  });

/** Single hotel's orders summary (outOfCatalog has no commission at this scope). */
export const useHotelPartnerOrdersSummary = (
  hotelId: string | undefined,
  from: string,
  to: string,
) =>
  useQuery<HotelPartnerOrdersSummary>({
    queryKey: [
      ...partnerOrdersKeys.all,
      "hotel-summary",
      hotelId || "",
      from,
      to,
    ],
    enabled: Boolean(hotelId),
    queryFn: () =>
      partnerSettingsService.getPartnerOrdersSummary(hotelId!, { from, to }),
    staleTime: 60 * 1000,
  });

/** Tenant-wide orders summary — feeds the orders-table section, distinct
 * from the `total-revenue` stat card (both carry `outOfCatalog`, per
 * different endpoints). */
export const useTenantPartnerOrdersSummary = (
  tenantId: string | undefined,
  from: string,
  to: string,
) =>
  useQuery<TenantPartnerOrdersSummary>({
    queryKey: [
      ...partnerOrdersKeys.all,
      "tenant-summary",
      tenantId || "",
      from,
      to,
    ],
    enabled: Boolean(tenantId),
    queryFn: () =>
      partnerDashboardService.getOrdersSummary(tenantId!, { from, to }),
    staleTime: 60 * 1000,
  });
